/**
 * Duplicate-resolution rules, client side. Mirrors server/resolve.js: an ordered
 * list of rules over the file's own columns, plus a tiebreak for the rows no
 * rule could separate.
 *
 * The rule *catalogue* (what "newest date" means, which rules need a column or a
 * value list) comes from /api/meta, so there is one definition of it and the
 * panel can never offer a rule the server would reject. Only the plumbing —
 * ids for React keys, canonical shape, reconciling against a changed file —
 * lives here.
 */

let nextId = 1
export const newId = () => nextId++

export const emptyResolution = () => ({ rules: [], tiebreak: 'first', merge: false })

/** A rule either ranks whole copies, or answers for one field on its own. */
export const isFieldRule = (rule) => rule?.scope === 'field'

/**
 * The catalogue entry for a rule. If meta has not arrived, fall back to trusting
 * the rule's own shape rather than dropping it on the floor.
 */
const prefOf = (preferences, rule) =>
  preferences?.find((p) => p.id === rule?.prefer) ?? {
    id: rule?.prefer,
    column: rule?.column !== undefined,
    values: Array.isArray(rule?.values) && rule.values.length > 0,
    dates: true,
    cased: true,
  }

/** A new rule starts on the first column, which the user then changes. */
export function newPreferRule(columns, preferences, prefer) {
  const id = prefer ?? preferences?.[0]?.id ?? 'newest'
  return {
    id: newId(),
    prefer: id,
    column: columns[0]?.name ?? '',
    values: [],
    dayFirst: false,
    caseSensitive: false,
    scope: 'row',
  }
}

export const countPreferRules = (resolution) => resolution?.rules?.length ?? 0

/**
 * Strip ids and anything the chosen rule does not use, the same way the server
 * does before it compiles. A rule missing the column or values it needs is
 * half-built, not an error, so it is dropped rather than sent.
 *
 * @returns {null | {rules, tiebreak}} null when the spec says nothing the
 *   default does not already do — keep the first copy in the file — so an
 *   untouched panel never changes a request or forces a rescan.
 */
export function normalizeResolution(resolution, preferences) {
  if (!resolution) return null
  const tiebreak = resolution.tiebreak === 'last' ? 'last' : 'first'
  const merge = !!resolution.merge
  const rules = []

  for (const rule of resolution.rules ?? []) {
    const pref = prefOf(preferences, rule)
    if (!rule?.prefer) continue
    const out = { prefer: rule.prefer }
    if (pref.column) {
      if (!rule.column) continue
      out.column = rule.column
      if (rule.scope === 'field') out.scope = 'field'
    }
    if (pref.values) {
      // Order is the rule, so it is preserved rather than sorted.
      const values = (rule.values ?? []).map((v) => String(v ?? '').trim()).filter(Boolean)
      if (values.length === 0) continue
      out.values = values
    }
    if (pref.dates && rule.dayFirst) out.dayFirst = true
    if (pref.cased && rule.caseSensitive) out.caseSensitive = true
    rules.push(out)
  }

  if (rules.length === 0 && tiebreak === 'first' && !merge) return null
  return merge ? { rules, tiebreak, merge: true } : { rules, tiebreak }
}

/** Comparable string form: two resolutions with the same signature keep the same rows. */
export const resolutionSignature = (resolution, preferences) =>
  JSON.stringify(normalizeResolution(resolution, preferences))

/** What goes on the wire — null means "no resolution", which the server reads as the default. */
export const toRequestResolution = normalizeResolution

/**
 * How each rule reads in a sentence, rather than as the dropdown option it was
 * picked from: the panel offers "newest date" over a column, but what the rule
 * *means* is "the newest last_contacted". Keyed by the catalogue's own ids, with
 * a fallback so an id this build has not heard of still produces a real phrase.
 */
const PLAIN_RULE = {
  newest: (col) => `the newest ${col}`,
  oldest: (col) => `the oldest ${col}`,
  max: (col) => `the largest ${col}`,
  min: (col) => `the smallest ${col}`,
  values: (col) => `the best ${col} on your list`,
  non_empty: (col) => `${col} filled in`,
  longest: (col) => `the longest ${col}`,
  shortest: (col) => `the shortest ${col}`,
  first_alpha: (col) => `${col} earliest A→Z`,
  last_alpha: (col) => `${col} latest A→Z`,
  most_filled: () => 'the most fields filled in',
}

const plainRule = (rule, preferences) => {
  const pref = prefOf(preferences, rule)
  const col = rule?.column || 'that column'
  const say = PLAIN_RULE[pref?.id]
  if (say) return say(col)
  // An unknown rule type still has a label and a column to fall back on.
  return pref?.column ? `${pref?.label ?? pref?.id} in ${col}` : (pref?.label ?? String(pref?.id))
}

const joinSteps = (steps) =>
  steps.length === 1 ? steps[0] : `${steps.slice(0, -1).join(', then ')}, then ${steps.at(-1)}`

/**
 * The whole resolution said out loud, for someone who did not build it. Returns
 * short independent sentences rather than one long one, so the panel can stack
 * them and the reader can stop after the first if that is all they wanted.
 *
 * Deliberately describes what is *selected right now*, including the rules that
 * are not doing anything yet — a rule the user can see in the list but not in
 * the summary would read as a bug.
 */
export function summarizeResolution(resolution, preferences) {
  const merge = !!resolution?.merge
  const rules = resolution?.rules ?? []
  // Field rules only mean anything while a record is being assembled.
  const rowRules = merge ? rules.filter((r) => !isFieldRule(r)) : rules
  const fieldRules = merge ? rules.filter(isFieldRule) : []
  const tie = resolution?.tiebreak === 'last' ? 'the last copy in the file' : 'the first copy in the file'
  const lines = []

  if (merge) {
    lines.push('Each duplicate becomes one record, built field by field.')
    lines.push(
      rowRules.length === 0
        ? 'Every field is filled from the first copy that has a value.'
        : `Fields come from the copy with ${joinSteps(rowRules.map((r) => plainRule(r, preferences)))} — and anything still blank falls through to the next copy that has a value.`,
    )
    for (const rule of fieldRules) {
      lines.push(`${rule.column || 'One field'} always comes from the copy with ${plainRule(rule, preferences)}.`)
    }
  } else {
    lines.push('One whole copy is kept and the rest are dropped.')
    lines.push(
      rowRules.length === 0
        ? `The copy kept is ${tie}.`
        : `The copy kept is the one with ${joinSteps(rowRules.map((r) => plainRule(r, preferences)))}.`,
    )
  }

  if (rowRules.length > 0) lines.push(`If nothing separates two copies, ${tie} wins.`)
  return lines
}

/** One line for the status bar, matching the server's view summary. */
export function describeResolution(resolution, preferences) {
  const spec = normalizeResolution(resolution, preferences)
  if (!spec) return null
  const tie = spec.tiebreak === 'last' ? 'last copy' : 'first copy'
  const how = spec.merge ? 'merged' : 'resolved'
  if (spec.rules.length === 0) return spec.merge ? 'merged into one record' : `keeping the ${tie}`
  return `${how} by ${spec.rules.length} rule${spec.rules.length === 1 ? '' : 's'}`
}

/**
 * Rebuild a saved resolution against the columns the file has now. A rule whose
 * column is gone is dropped — sending it would have the server reject the whole
 * request — and the count says how many, so the user can be told.
 *
 * @returns {{resolution: object, dropped: number}}
 */
export function reconcileResolution(resolution, headers, preferences) {
  const known = new Set(headers)
  let dropped = 0
  const rules = []

  for (const rule of resolution?.rules ?? []) {
    const pref = prefOf(preferences, rule)
    if (pref.column && !known.has(rule?.column)) {
      dropped++
      continue
    }
    rules.push({
      values: [],
      dayFirst: false,
      caseSensitive: false,
      column: '',
      scope: 'row',
      ...rule,
      id: newId(),
    })
  }

  return {
    resolution: {
      rules,
      tiebreak: resolution?.tiebreak === 'last' ? 'last' : 'first',
      merge: !!resolution?.merge,
    },
    dropped,
  }
}
